import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Catalogue, Indicator, MeasureRef, errorMessage, indicatorRegistryApi,
} from '../../../services/indicatorRegistryApi';
import ResultsTable from './ResultsTable';
import StatusChip from './StatusChip';

const LAST_YEAR = new Date().getFullYear() - 1;
const YEARS = Array.from({ length: 6 }, (_, i) => LAST_YEAR + 1 - i);

function describeSide(side: MeasureRef, catalogue: Catalogue): string {
  const measure = catalogue.measures.find(m => m.code === side.measure);
  const filters = Object.entries(side.filters).map(([dim, values]) => {
    const d = catalogue.dimensions.find(x => x.code === dim);
    const labels = values.map(v => d?.values.find(x => x.code === v)?.label || v);
    return `${d?.label.toLowerCase() || dim}: ${labels.join(' or ')}`;
  });
  if (side.year_offset) filters.push(side.year_offset < 0 ? 'the year before' : 'the year after');
  return `${measure?.label || side.measure}${filters.length ? ` (${filters.join('; ')})` : ''}`;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{children}</Typography>
  </Box>
);

interface Props {
  catalogue: Catalogue;
  id: number;
  onBack: () => void;
  onEdit: (indicator: Indicator) => void;
  onOpen: (id: number) => void;
}

const IndicatorDetail: React.FC<Props> = ({ catalogue, id, onBack, onEdit, onOpen }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [publishYear, setPublishYear] = useState(LAST_YEAR);
  const [view, setView] = useState({ year: LAST_YEAR, geo_level: 'district', breakdown: '' });

  const { data: ind, isLoading, error } = useQuery(['indicator-registry', id], () => indicatorRegistryApi.get(id));
  const { data: versions = [] } = useQuery(
    ['indicator-registry', 'versions', ind?.code],
    () => indicatorRegistryApi.versions(ind!.code),
    { enabled: !!ind },
  );
  const published = ind?.status === 'approved' || ind?.status === 'retired';
  const { data: results = [], isFetching: loadingResults } = useQuery(
    ['indicator-registry', id, 'results', view],
    () => indicatorRegistryApi.results(id, view),
    { enabled: published },
  );
  const { data: runs = [] } = useQuery(['indicator-registry', id, 'runs'], () => indicatorRegistryApi.runs(id), { enabled: published });

  const refresh = () => queryClient.invalidateQueries(['indicator-registry']);

  const transition = useMutation({
    mutationFn: (action: 'submit' | 'approve' | 'reject' | 'retire' | 'new-version') =>
      indicatorRegistryApi.transition(id, action, action === 'reject' ? note : undefined),
    onSuccess: (updated, action) => {
      refresh();
      setRejecting(false);
      const messages = {
        submit: 'Sent for review', approve: 'Approved', reject: 'Sent back to the author',
        retire: 'Retired', 'new-version': `Version ${updated.version} started`,
      };
      enqueueSnackbar(messages[action], { variant: 'success' });
      if (action === 'new-version') onEdit(updated);
    },
    onError: (err) => enqueueSnackbar(errorMessage(err), { variant: 'error' }),
  });

  const remove = useMutation({
    mutationFn: () => indicatorRegistryApi.remove(id),
    onSuccess: () => { refresh(); enqueueSnackbar('Draft deleted', { variant: 'success' }); onBack(); },
    onError: (err) => enqueueSnackbar(errorMessage(err), { variant: 'error' }),
  });

  const compute = useMutation({
    mutationFn: () => indicatorRegistryApi.compute(id, publishYear),
    onSuccess: (run) => {
      refresh();
      setView(v => ({ ...v, year: publishYear }));
      enqueueSnackbar(`Published ${run.rows.toLocaleString()} values for ${run.year}`, { variant: 'success' });
    },
    onError: (err) => enqueueSnackbar(errorMessage(err), { variant: 'error' }),
  });

  if (isLoading) return <CircularProgress />;
  if (error || !ind) return <Alert severity="error">{errorMessage(error)}</Alert>;

  const canApprove = catalogue.can_approve;
  const dim = (code: string) => catalogue.dimensions.find(d => d.code === code);
  const rows = results.map(r => ({
    ...r,
    breakdown_label: r.breakdown ? dim(r.breakdown)?.values.find(v => v.code === r.breakdown_value)?.label || 'Not stated' : '',
  }));
  const busy = transition.isLoading || compute.isLoading || remove.isLoading;

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>All indicators</Button>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline">{ind.code} · version {ind.version}</Typography>
              <StatusChip status={ind.status} />
            </Stack>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{ind.name}</Typography>
            {versions.length > 1 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                {versions.map(v => (
                  <Chip
                    key={v.id} size="small" label={`v${v.version}`}
                    color={v.id === ind.id ? 'primary' : 'default'}
                    variant={v.id === ind.id ? 'filled' : 'outlined'}
                    onClick={() => onOpen(v.id)}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {ind.status === 'draft' && (
              <>
                <Button color="error" disabled={busy} onClick={() => window.confirm(`Delete draft ${ind.code} v${ind.version}?`) && remove.mutate()}>
                  Delete
                </Button>
                <Button variant="outlined" disabled={busy} onClick={() => onEdit(ind)}>Edit</Button>
                <Button variant="contained" disabled={busy} onClick={() => transition.mutate('submit')}>Send for review</Button>
              </>
            )}
            {ind.status === 'in_review' && canApprove && (
              <>
                <Button variant="outlined" disabled={busy} onClick={() => setRejecting(true)}>Send back</Button>
                <Button variant="contained" color="success" disabled={busy} onClick={() => transition.mutate('approve')}>Approve</Button>
              </>
            )}
            {ind.status === 'approved' && canApprove && (
              <Button color="warning" disabled={busy} onClick={() => window.confirm(`Retire ${ind.code}? Its published values stay available.`) && transition.mutate('retire')}>
                Retire
              </Button>
            )}
            {published && (
              <Button variant="outlined" disabled={busy} onClick={() => transition.mutate('new-version')}>Start new version</Button>
            )}
          </Stack>
        </Box>

        {ind.status === 'in_review' && !canApprove && (
          <Alert severity="info" sx={{ mt: 2 }}>Waiting for an approver. The definition can't change while it is in review.</Alert>
        )}
        {ind.status === 'draft' && ind.review_note && (
          <Alert severity="warning" sx={{ mt: 2 }}>Sent back by the reviewer: {ind.review_note}</Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            <Field label="Definition">{ind.definition}</Field>
            {ind.formula ? (
              <Field label="Calculation">
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 600 }}>{ind.formula}</Box>
                {Object.keys(ind.terms).sort().map(name => (
                  <Box component="span" key={name} sx={{ display: 'block', mt: 0.5 }}>
                    <b>{name}</b> = {ind.terms[name].label ? `${ind.terms[name].label}: ` : ''}{describeSide(ind.terms[name], catalogue)}
                  </Box>
                ))}
                {Object.keys(ind.terms).length > 1 && (
                  <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                    {ind.match_schools ? 'Only schools that report every part.' : 'All schools for each part.'}
                  </Box>
                )}
              </Field>
            ) : (
            <Field label="Calculation">
              {describeSide(ind.numerator, catalogue)}
              {ind.denominator && <> ÷ {describeSide(ind.denominator, catalogue)}</>}
              {ind.multiplier !== 1 && <> × {ind.multiplier}</>}
              {ind.denominator && !ind.match_schools && ' — all schools on each side'}
              {ind.denominator && ind.match_schools && ' — only schools that report both'}
            </Field>
            )}
            {ind.rationale && <Field label="Rationale">{ind.rationale}</Field>}
            {ind.interpretation && <Field label="How to read it">{ind.interpretation}</Field>}
            {ind.limitations && <Field label="Limitations">{ind.limitations}</Field>}
          </Grid>
          <Grid item xs={12} md={5}>
            <Field label="Unit">{catalogue.units.find(u => u.code === ind.unit)?.label}, {ind.decimals} decimal{ind.decimals === 1 ? '' : 's'}</Field>
            <Field label="Category">{catalogue.categories.find(c => c.code === ind.category)?.label}</Field>
            <Field label="Breakdowns">
              {['Region', 'District', 'School', ...ind.breakdowns.map(b => dim(b)?.label || b)].join(', ')}
            </Field>
            {ind.thresholds.length > 0 && (
              <Field label="Thresholds">
                {ind.thresholds.map(t => `${t.status}: ${t.min ?? '…'} to ${t.max ?? '…'}`).join('\n')}
              </Field>
            )}
            {ind.sdg_code && <Field label="SDG 4">{ind.sdg_code}</Field>}
            <Field label="Author">{ind.created_by_name || 'System (migrated)'}</Field>
            {ind.approved_at && (
              <Field label="Approved">
                {new Date(ind.approved_at).toLocaleDateString()}{ind.approved_by_name ? ` by ${ind.approved_by_name}` : ''}
              </Field>
            )}
          </Grid>
        </Grid>
      </Paper>

      {published && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Published values</Typography>
            {ind.status === 'approved' && canApprove && (
              <Stack direction="row" spacing={1}>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel>Year</InputLabel>
                  <Select label="Year" value={publishYear} onChange={e => setPublishYear(Number(e.target.value))}>
                    {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button
                  variant="contained" disabled={busy} onClick={() => compute.mutate()}
                  startIcon={compute.isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {compute.isLoading ? 'Publishing…' : 'Compute and publish'}
                </Button>
              </Stack>
            )}
          </Box>

          {runs[0] && (
            <Alert severity={runs[0].status === 'failed' ? 'error' : 'info'} sx={{ mb: 2 }}>
              Last run: {runs[0].year}, {new Date(runs[0].started_at).toLocaleString()} —{' '}
              {runs[0].status === 'failed' ? runs[0].error : `${runs[0].rows.toLocaleString()} values`}
              {runs[0].warnings.map(w => <Box key={w} component="span" sx={{ display: 'block' }}>{w}</Box>)}
            </Alert>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Year</InputLabel>
              <Select label="Year" value={view.year} onChange={e => setView(v => ({ ...v, year: Number(e.target.value) }))}>
                {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Level</InputLabel>
              <Select label="Level" value={view.geo_level} onChange={e => setView(v => ({ ...v, geo_level: e.target.value }))}>
                {catalogue.geo_levels.map(g => <MenuItem key={g.code} value={g.code}>{g.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel shrink>Breakdown</InputLabel>
              <Select label="Breakdown" displayEmpty notched value={view.breakdown} onChange={e => setView(v => ({ ...v, breakdown: e.target.value }))}>
                <MenuItem value="">None</MenuItem>
                {ind.breakdowns.map(b => <MenuItem key={b} value={b}>{dim(b)?.label || b}</MenuItem>)}
              </Select>
            </FormControl>
            {loadingResults && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
          </Stack>

          <ResultsTable
            rows={rows}
            unit={ind.unit}
            decimals={ind.decimals}
            hasDenominator={!!ind.denominator || /÷|\//.test(ind.formula || '')}
            breakdownLabel={view.breakdown ? dim(view.breakdown)?.label : undefined}
            maxHeight={520}
          />
        </Paper>
      )}

      <Dialog open={rejecting} onClose={() => setRejecting(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send {ind.code} back to the author</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth multiline minRows={3} sx={{ mt: 1 }}
            label="What needs to change?" value={note} onChange={e => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(false)}>Cancel</Button>
          <Button variant="contained" disabled={!note.trim() || busy} onClick={() => transition.mutate('reject')}>Send back</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IndicatorDetail;
